import React from 'react';
import { useIntl } from 'react-intl';
import { DashboardWidget } from './DashboardWidget';

interface IBusinessAlert {
  type: 'warning' | 'danger' | 'info';
  title: string;
  message: string;
  clientId?: number;
}

interface Props {
  data: IBusinessAlert[];
  loading: boolean;
  onClientClick?: (clientId: number) => void;
}

const ALERT_STYLES: Record<string, { color: string; bg: string; icon: string }> = {
  danger: { color: '#ef4444', bg: 'rgba(239,68,68,0.08)', icon: 'pi pi-exclamation-circle' },
  warning: { color: '#f59e0b', bg: 'rgba(245,158,11,0.08)', icon: 'pi pi-exclamation-triangle' },
  info: { color: '#4d7c0f', bg: 'rgba(163, 230, 53, 0.18)', icon: 'pi pi-info-circle' }
};

export const BusinessAlertsWidget: React.FC<Props> = ({ data, loading, onClientClick }) => {
  const intl = useIntl();

  return (
    <DashboardWidget
      title={intl.formatMessage({ id: 'dashboard.alerts' })}
      icon="pi pi-bell"
      iconColor="#f59e0b"
      loading={loading}
      badge={data.length || null}
      minHeight="200px"
    >
      {data.length === 0 ? (
        <div className="flex align-items-center gap-2" style={{ padding: '1rem 0' }}>
          <i className="pi pi-check-circle" style={{ color: '#22c55e', fontSize: '1.2rem' }} />
          <span style={{ color: 'var(--ios-text-secondary)', fontSize: '0.85rem' }}>
            {intl.formatMessage({ id: 'dashboard.noAlerts' })}
          </span>
        </div>
      ) : (
        <div className="flex flex-column gap-2">
          {data.slice(0, 5).map((alert, i) => {
            const style = ALERT_STYLES[alert.type] ?? ALERT_STYLES['info']!;
            return (
              <div
                key={i}
                className="flex align-items-start gap-2"
                style={{
                  padding: '0.5rem',
                  borderRadius: 'var(--ios-radius-md)',
                  background: style.bg,
                  borderLeft: `3px solid ${style.color}`,
                  cursor: alert.clientId && onClientClick ? 'pointer' : 'default'
                }}
                onClick={() => alert.clientId && onClientClick?.(alert.clientId)}
              >
                <i className={style.icon} style={{ color: style.color, fontSize: '0.85rem', marginTop: '0.1rem' }} />
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.78rem', color: style.color }}>{alert.title}</div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--ios-text-secondary)' }}>{alert.message}</div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </DashboardWidget>
  );
};
